import { LoadingButton } from "@mui/lab";
import { Box, Typography, useTheme } from "@mui/material";
import { isEqual } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconButton } from "../../atoms/IconButton";
import { InputOnFocus } from "../../atoms/InputOnFocus";
import { NavbarIcon } from "../../atoms/navbar/Icon";
import { WizardFooter } from "../../atoms/WizardFooter";
import { navigateToUseCase } from "../../components/UseCaseNavigator";
import { useQuestionnaire } from "../../hooks/useQuestionnaires";
import { UseCaseWizardLoader } from "../../layouts/skeleton-loaders/UseCaseWizardLoader";
import {
  CustomAnswer,
  Question,
  QuestionLogicCondition,
  QuestionLogicOperator,
  QuestionLogicTestOperator,
  QuestionLogicThen,
  UseCase,
} from "../../models/types";
import ErrorBoundaryWrapper from "../../molecules/ErrorHandling";
import { QuestionDisplay } from "../../molecules/impact-assessment/QuestionDisplay";
import { QuestionnaireProgressBar } from "../../molecules/impact-assessment/QuestionnaireProgressBar";
import { QuestionnaireStepper } from "../../molecules/impact-assessment/QuestionnaireStepper";
import { WizardQuestionDisplay } from "../../molecules/impact-assessment/WizardQuestionDisplay";
import { ShowMore } from "../../molecules/ShowMore";
import { TableTextWithEllipsis } from "../../molecules/TableTextWithEllipsis";
import { QuestionIconIndex } from "../../molecules/template-builder/QuestionIconIndex";
import { QuestionnaireBuilderQuestionGroup } from "../../molecules/template-builder/QuestionnaireBuilderQuestionGroup";
import { QuestionnaireBuilderQuestionTitle } from "../../molecules/template-builder/QuestionnaireBuilderQuestionTitle";
import { setQuickLinkCompleted } from "../../services/IntercomService";
import { deleteUseCase, editUseCase } from "../../services/UseCaseService";
import {
  getDefaultCustomFieldStartValue,
  ScreenSmallerThen,
  validateURL,
} from "../../utilities/UIHelper";
import { QuestionGroup } from "../custom-fields/QuestionGroup";

type UseCaseWizardProps = {
  useCase?: UseCase;
  templateId: string;
};

type QuestionIndexBySection = {
  [key: string]: {
    value: number;
  };
};

type QuestionIndexToTargetMap = {
  [questionIndex: number]: number;
};

export const UseCaseWizard = (props: UseCaseWizardProps) => {
  const { useCase, templateId } = props;
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: questionnaire, isLoading: isLoadingQuestionnaire } = useQuestionnaire(templateId, {
    create_session: "true",
  });
  const [sessionId, setSessionId] = useState<string>("");
  const [questionIndex, setQuestionIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [answerLoading, setAnswersLoading] = useState<boolean>(true);

  const [sections, setSections] = useState<string[]>([]);
  const questionFieldRef = useRef();
  const [answers, setAnswers] = useState<CustomAnswer[]>([]);
  const [hasQuestionAnswer, setHasQuestionAnswer] = useState<boolean>(false);

  const flattenQuestions = (qs: Question[]): Question[] =>
    qs.flatMap((q) => [q, ...(q.questions ? flattenQuestions(q.questions) : [])]);

  const allQuestionsWithGroup = useMemo(
    () => flattenQuestions(questionnaire?.questions ?? []),
    [questionnaire?.questions]
  );

  const leafQuestions = useMemo(
    () =>
      allQuestionsWithGroup.filter(
        (q) => (!q.questions || q.questions.length === 0) && q.type !== "Question Group"
      ),
    [allQuestionsWithGroup]
  );

  const currentStepsBySection = leafQuestions
    ?.filter((q) => q?.navigation_tag === leafQuestions[questionIndex]?.navigation_tag)
    .map((q) => q?.sub_section);
  const [firstQuestionIndexBySection, setFirstQuestionIndexBySection] =
    useState<QuestionIndexBySection>({});
  const [completed, setCompleted] = useState<boolean>(false);
  const [quickLink, setQuickLink] = useState<string | null>(null);
  const queryParams = new URLSearchParams(location.search);
  const [questionsListExpanded, setQuestionsListExpanded] = useState<boolean>(false);
  const [questionToTargetMap, setQuestionToTargetMap] = useState<QuestionIndexToTargetMap>({});

  let currentIndex = 1;

  useEffect(() => {
    if (questionnaire) {
      const uniqueSections = Array.from(
        new Set(questionnaire.questions.map((q) => q?.navigation_tag))
      );
      const sectionIndexes: Record<string, { value: number }> = {};
      questionnaire.questions.forEach((item, index) => {
        if (item?.navigation_tag) {
          if (!(item?.navigation_tag in sectionIndexes)) {
            sectionIndexes[item?.navigation_tag] = { value: index };
          }
        }
      });
      const flattenQuestions = (qs: Question[]): Question[] =>
        qs.flatMap((q) => [q, ...(q.questions ? flattenQuestions(q.questions) : [])]);
      const allQuestionsWithGroup = flattenQuestions(questionnaire?.questions ?? []);
      const leafQuestions = allQuestionsWithGroup.filter(
        (q) => (!q.questions || q.questions.length === 0) && q.type !== "Question Group"
      );
      setFirstQuestionIndexBySection(sectionIndexes);
      setSections(uniqueSections.filter((section): section is string => section !== undefined));
      const newAnswers = leafQuestions?.map((question: any) => ({
        questionnaire: questionnaire.id,
        question: question?.ref ?? question.title,
        field_type: question.type,
        answer: getDefaultCustomFieldStartValue(question.type),
        status: "DRAFT",
        related_object_id: useCase?.id,
        related_object_type: "Use Case",
        action_metadata: question.metadata?.action,
      }));
      setAnswers(newAnswers);
      if (questionnaire.session_id) {
        setSessionId(questionnaire.session_id);
      }
      setAnswersLoading(false);
    }
    setCompleted(false);
  }, [questionnaire]);

  useEffect(() => {
    try {
      if (queryParams.has("quick_link")) {
        const quickLinkId = queryParams.get("quick_link");
        if (quickLinkId) {
          setQuickLink(quickLinkId);
        }
      }
    } catch (e) {}
  }, [queryParams]);

  const handleChange = (value: string | null, models?: string) => {
    // setValueError(false);
    const newAnswers = answers.map((answer, index) => {
      if (index === questionIndex) {
        return {
          ...answer,
          answer: value ? (typeof value === "string" ? value : JSON.stringify(value)) : "",
        };
      }
      if (models && answer.question === "use_case_models") {
        return {
          ...answer,
          answer: models,
        };
      }
      return answer;
    });
    if (
      newAnswers.some(
        (answer) =>
          answer.answer && answer.answer !== getDefaultCustomFieldStartValue(answer.field_type)
      )
    ) {
      setHasQuestionAnswer(true);
    }
    setAnswers(newAnswers);
  };

  const onFinish = async () => {
    try {
      if (useCase) {
        await editUseCase(useCase.id, { status: "STABLE" });
        navigateToUseCase({
          navigate,
          useCaseId: useCase?.id,
          useCaseNumber: useCase?.use_case_num,
        });
      }
      if (quickLink) {
        setQuickLinkCompleted(quickLink);
      }
      if (
        questionnaire?.metadata?.end_screen?.redirect &&
        questionnaire?.metadata?.end_screen?.redirect_url
      ) {
        const rawUrl = questionnaire.metadata.end_screen.redirect_url.trim();
        const normalizedUrl =
          rawUrl.startsWith("http://") || rawUrl.startsWith("https://")
            ? rawUrl
            : `https://${rawUrl}`;

        if (validateURL(normalizedUrl)) {
          window.open(normalizedUrl, "_blank");
        } else {
          console.warn(`Invalid redirect URL: ${normalizedUrl}`);
        }
      }
    } catch {}
  };

  const handleLogic = (): QuestionLogicThen | null => {
    const logics = leafQuestions[questionIndex].logic;
    if (!logics?.length) {
      if (questionIndex === leafQuestions.length - 1) {
        return {
          action: "finish",
          value: "",
        };
      } else {
        return {
          action: "go_to",
          value: leafQuestions[questionIndex + 1]?.ref || "",
        };
      }
    }

    // Safely parse JSON literals that may be stored as strings.
    const parseVal = (v: any) => {
      if (v === null || v === undefined) return v;
      try {
        return typeof v === "string" ? JSON.parse(v) : v;
      } catch {
        return v;
      }
    };

    // Primitive comparison helpers.
    const evaluatePrimitive = (
      left: any,
      right: any,
      op: QuestionLogicTestOperator | undefined
    ): boolean => {
      switch (op) {
        case "==":
          return left === right;
        case "!=":
          return left !== right;
        case ">":
          return left > right;
        case "<":
          return left < right;
        case ">=":
          return left >= right;
        case "<=":
          return left <= right;
        case "contains":
          return Array.isArray(left) ? left.includes(right) : `${left}`.includes(`${right}`);
        case "does_not_contain":
          return Array.isArray(left) ? !left.includes(right) : !`${left}`.includes(`${right}`);
        case "begins_with":
          return `${left}`.startsWith(`${right}`);
        case "does_not_begin_with":
          return !`${left}`.startsWith(`${right}`);
        case "ends_with":
          return `${left}`.endsWith(`${right}`);
        case "does_not_end_with":
          return !`${left}`.endsWith(`${right}`);
        case "is_null":
          return left === null || left === undefined || left === "";
        case "is_not_null":
          return left !== null && left !== undefined && left !== "";
        default:
          return false;
      }
    };

    // Recursively evaluate a condition node.
    const satisfies = (cond: QuestionLogicCondition): boolean => {
      // If the node has child conditions, treat it as a group.
      if (cond.condition && cond.condition.length) {
        const op: QuestionLogicOperator = cond.operator ?? "and";
        if (op === "and") {
          return cond.condition.every((c) => satisfies(c));
        }
        // op === "or"
        return cond.condition.some((c) => satisfies(c));
      }

      // Leaf rule: must have test_question defined.
      if (!cond.test_question) return false;

      const relatedAnswer = answers.find((a) => a.question === cond.test_question);
      if (!relatedAnswer) return false;

      const answerVal = parseVal(relatedAnswer.answer);
      const testVal = parseVal(cond.test_value);
      return evaluatePrimitive(answerVal, testVal, cond.test_operator);
    };

    // Iterate over each logic rule for the current question.
    for (const logicItem of logics) {
      if (satisfies(logicItem.condition)) {
        return logicItem.then; // First matching rule wins.
      }
    }
    return null;
  };

  const isSmallScreen = ScreenSmallerThen("1100px");

  return questionnaire && !answerLoading ? (
    <ErrorBoundaryWrapper>
      <Box display="flex" flexDirection="column" height="100vh" overflow="hidden">
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          bgcolor={theme.palette.custom.primaryBackground}
          alignItems="center"
          padding="20px"
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="flex-start"
            flexGrow={1}
            justifyContent="flex-start"
          >
            <IconButton
              variant="left-arrow"
              height="20px"
              width="20px"
              color={theme.palette.custom.secondaryTypography}
              onClick={async () => {
                if (useCase) {
                  if (!hasQuestionAnswer) {
                    await deleteUseCase(useCase?.id);
                    navigate("/oversight/use-cases");
                  }
                  if (useCase.status === "DRAFT") {
                    navigate("/oversight/use-cases");
                  } else {
                    navigateToUseCase({
                      navigate,
                      useCaseId: useCase.id,
                      useCaseNumber: useCase.use_case_num,
                    });
                  }
                } else {
                  navigate(-1);
                }
              }}
            />
            <Box display="flex" flexDirection="column" flexGrow={1} gap="5px">
              {useCase && (
                <>
                  {" "}
                  <InputOnFocus
                    value={useCase.name}
                    onChange={async (newName: string) => {
                      await editUseCase(useCase.id, { name: newName });
                    }}
                  />
                  <Typography variant="body2">Use Case-{useCase?.use_case_num}</Typography>
                </>
              )}
              {!useCase && (
                <TableTextWithEllipsis
                  value={questionnaire.name}
                  variant={"h1"}
                  fontSize={22}
                  expanded={true}
                />
              )}
            </Box>
          </Box>
        </Box>
        <QuestionnaireProgressBar
          rounded={false}
          questionIndex={questionIndex}
          questionnaireLength={leafQuestions.length}
        />
        <Box display="flex" flexDirection="row" flex="1" overflow="hidden">
          {!questionsListExpanded && (
            <Box padding="20px">
              <Box
                display="flex"
                padding="4px"
                width="40px"
                height="40px"
                borderRadius="50%"
                bgcolor={theme.palette.custom.blue[50]}
                alignItems="center"
                justifyContent="center"
                sx={{ cursor: "pointer" }}
                onClick={() => setQuestionsListExpanded(true)}
              >
                <NavbarIcon
                  variant="lists"
                  sx={{
                    width: "20px",
                    height: "20px",
                    minHeight: "20px",
                    minWidth: "20px",
                  }}
                />
              </Box>
            </Box>
          )}
          {questionsListExpanded && (
            <Box
              display="flex"
              flexDirection="column"
              maxWidth="304px"
              minWidth="304px"
              flex={1}
              padding="16px"
              gap="12px"
              borderRight={`1px solid ${theme.palette.custom.secondaryBorder}`}
              bgcolor={theme.palette.custom.primaryBackground}
              overflow="auto"
            >
              <Box
                onClick={() => setQuestionsListExpanded(false)}
                sx={{ cursor: "pointer" }}
                display="flex"
                alignItems="center"
              >
                <NavbarIcon
                  variant="left-arrow"
                  color={theme.palette.custom.blueTypography}
                  sx={{ width: "24px", minWidth: "24px", height: "24px", minHeight: "24px" }}
                />
              </Box>
              <Typography variant="h4">Questions</Typography>
              <Box width="100%" display="flex" flexDirection="column" gap="8px">
                {questionnaire?.questions.map((question, idx) => {
                  if (question.type === "Question Group") {
                    const groupStartIndex = currentIndex;
                    currentIndex += question.questions?.length ?? 0;
                    return (
                      <QuestionnaireBuilderQuestionGroup
                        key={question.ref}
                        question={question}
                        selected={isEqual(leafQuestions[questionIndex], question)}
                        selectedQuestion={leafQuestions[questionIndex]}
                        onClick={() => {}}
                        index={groupStartIndex}
                      />
                    );
                  } else {
                    const thisIndex = currentIndex++;
                    return (
                      <QuestionnaireBuilderQuestionTitle
                        key={question.ref}
                        question={question}
                        selected={isEqual(leafQuestions[questionIndex], question)}
                        onClick={() => {}}
                        index={thisIndex}
                      />
                    );
                  }
                })}
              </Box>
            </Box>
          )}
          <Box display="flex" flexDirection="column" flex="1" overflow="hidden">
            <Box
              display="flex"
              flexDirection="column"
              gap="16px"
              flex={1}
              justifyContent="center"
              alignItems="center"
              padding="20px"
              overflow="auto"
            >
              {completed ? (
                <Box
                  height="100%"
                  maxWidth="800px"
                  display="flex"
                  flexDirection="column"
                  gap="24px"
                  justifyContent="start"
                  paddingTop="80px"
                >
                  <Box display="flex" flexDirection="column" gap="4px">
                    <Typography variant="h1">
                      {questionnaire?.metadata?.end_screen?.title ?? "Finish Use Case Setup"}
                    </Typography>
                    <ShowMore
                      description={
                        questionnaire?.metadata?.end_screen?.description ??
                        "You're all set! Based on your approvals, we'll proceed with analyzing the selected resources to enhance your use case. You can review or update these approvals anytime."
                      }
                      richText
                      showFullDescription
                      showShowMoreButton={false}
                    />
                  </Box>
                  <Box display="flex">
                    <LoadingButton variant="contained" onClick={onFinish}>
                      Finish
                    </LoadingButton>
                  </Box>
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection={"column"}
                  gap="16px"
                  flexGrow={1}
                  width="100%"
                  alignItems="center"
                >
                  <QuestionnaireStepper
                    steps={
                      currentStepsBySection?.filter((step): step is string => step !== undefined) ||
                      []
                    }
                    questionnaire={questionnaire}
                    questionIndex={questionIndex}
                  />
                  <Box
                    display="flex"
                    flexDirection={"column"}
                    gap="16px"
                    flexGrow={1}
                    width="100%"
                    maxWidth="800px"
                  >
                    {leafQuestions[questionIndex]?.type === "Question Group" ? (
                      <Box display="flex" flexGrow={1} flexDirection="column" gap="16px">
                        <Box display="flex" width="100%">
                          <QuestionIconIndex
                            icon="audit-log"
                            bgcolor={theme.palette.custom.primaryBorder}
                            color={theme.palette.custom.primaryTypography}
                            index={leafQuestions[questionIndex]?.title}
                            width="unset"
                          />
                        </Box>
                        <QuestionDisplay
                          ref={questionFieldRef}
                          hideQuestionNumber
                          hideQuestionnaireName
                          question={leafQuestions[questionIndex]}
                          questionnaire={questionnaire}
                          questionIndex={questionIndex}
                          value={null}
                          onChange={handleChange}
                          valueError={false}
                          relatedObjectId={useCase?.id ?? ""}
                          handleNext={() => {}}
                          relatedObjectName={useCase?.name ?? ""}
                        />
                        <QuestionGroup
                          questions={leafQuestions[questionIndex].questions ?? []}
                          setSelectedQuestion={() => {}}
                          index={questionIndex + 1}
                        />
                      </Box>
                    ) : (
                      <WizardQuestionDisplay
                        questionnaire={questionnaire}
                        question={leafQuestions[questionIndex]}
                        questionIndex={questionIndex}
                        answer={answers[questionIndex]}
                        onChange={handleChange}
                        valueError={false}
                        relatedObjectId={useCase?.id ?? ""}
                        handleNext={() => {
                          const outcome = handleLogic();
                          let targetIdx: number | undefined;

                          if (outcome) {
                            if (outcome.action === "finish") {
                              setQuestionToTargetMap((prev) => ({
                                ...prev,
                                [questionIndex]: leafQuestions.length,
                              }));
                              setQuestionIndex(leafQuestions.length);
                              return setCompleted(true);
                            }
                            if (
                              outcome.action === "go_to" &&
                              outcome.value !== null &&
                              outcome.value !== undefined
                            ) {
                              targetIdx = leafQuestions.findIndex(
                                (q) => q.ref === outcome.value || q.title === outcome.value
                              );
                              if (targetIdx && targetIdx !== undefined && targetIdx !== -1) {
                                setQuestionToTargetMap((prev) => ({
                                  ...prev,
                                  [questionIndex]: targetIdx ?? questionIndex + 1,
                                }));
                                return setQuestionIndex(targetIdx);
                              }
                            }
                          }

                          // Default navigation when no rule matched.
                          if (questionIndex === leafQuestions.length - 1) {
                            setQuestionToTargetMap((prev) => ({
                              ...prev,
                              [questionIndex]: leafQuestions.length,
                            }));
                            setQuestionIndex(questionIndex + 1);
                            setCompleted(true);
                          } else {
                            // @TODO remove this hardcode
                            if (
                              leafQuestions[questionIndex].ref ===
                                "use_case_documentation_options" &&
                              answers[questionIndex].answer === JSON.stringify([])
                            ) {
                              setQuestionToTargetMap((prev) => ({
                                ...prev,
                                [questionIndex]: questionIndex + 2,
                              }));
                              return setQuestionIndex(questionIndex + 2);
                            }
                            setQuestionToTargetMap((prev) => ({
                              ...prev,
                              [questionIndex]: questionIndex + 1,
                            }));
                            setQuestionIndex(questionIndex + 1);
                          }
                        }}
                        loading={loading}
                        handlePrevious={() => {
                          const previousIndex = Object.entries(questionToTargetMap).find(
                            ([sourceIdx, targetIdx]) => Number(targetIdx) === questionIndex
                          )?.[0];

                          if (previousIndex !== undefined) {
                            setQuestionIndex(Number(previousIndex));
                          } else {
                            setQuestionIndex(questionIndex - 1);
                          }
                        }}
                        relatedObjectName={useCase?.name ?? ""}
                        sessionID={sessionId}
                        handleLogic={handleLogic}
                        questionGroup={
                          allQuestionsWithGroup.find((q) =>
                            q.questions?.some(
                              (child) => child.ref === leafQuestions[questionIndex]?.ref
                            )
                          )?.title ?? ""
                        }
                        allQuestions={leafQuestions}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            <WizardFooter />
          </Box>
        </Box>
      </Box>
    </ErrorBoundaryWrapper>
  ) : (
    <UseCaseWizardLoader />
  );
};
